/**
 * SOVRANOVA — SelfOnlyConsent (Ambient Companion)
 * ------------------------------------------------------------
 * The SAFE consent option — the one that needs no legal sign-off, because it
 * records NO ONE but the account holder. SOVRANOVA captures only the user's own
 * voice and reflections; a therapist's or partner's words are never recorded,
 * transcribed, or stored. This is the boundary that keeps capture-with-others-
 * present in the self-only lane (same footing as Reflect, already live).
 *
 * Recording another person verbatim is a different feature and stays gated
 * behind the full consent flow + legal review — a checkbox can't change that.
 */
import { useState } from 'react'
import { api } from '../../services/api'

export function SelfOnlyConsent({ onAcknowledge, onBack }) {
  const [busy, setBusy]   = useState(false)
  const [error, setError] = useState(null)

  const acknowledge = async () => {
    if (busy) return
    setBusy(true); setError(null)
    try {
      // Record a self-only consent: the only party captured is you, so consent
      // is trivially complete. Non-fatal — self capture doesn't depend on it.
      const res = await api.post('/ambient/consent', {
        method: 'self_only',
        participants: [{ name: 'You', role: 'self', consented: true }],
      })
      onAcknowledge?.(res?.consentId || null)
    } catch {
      onAcknowledge?.(null)
    } finally { setBusy(false) }
  }

  return (
    <div style={s.wrap}>
      <div style={s.top}><button style={s.back} onClick={onBack}>← Reflect</button></div>
      <div style={s.head}>
        <div style={s.icon}>🤝</div>
        <h1 style={s.title}>Your side only</h1>
        <p style={s.sub}>For when you want to reflect during or right after a session or a hard conversation.</p>
      </div>
      <div style={s.card}>
        <p style={s.body}>I only ever keep <strong>your</strong> voice and your reflections. I don't record, transcribe, or store anyone else's words — not your therapist's, not your partner's.</p>
        <p style={s.body}>So speak freely about your own experience: what you felt, what you noticed, what you'd like to work through. If others are nearby, it's kind to let them know you're taking personal notes.</p>
        <ul style={s.points}>
          <li style={s.point}>Only your voice is captured</li>
          <li style={s.point}>No one else is recorded or stored</li>
          <li style={s.point}>Private to you — deletable anytime</li>
        </ul>
        {error && <p style={s.error}>{error}</p>}
        <button style={s.primary} onClick={acknowledge} disabled={busy}>
          {busy ? 'One moment…' : 'I understand — capture only my side'}
        </button>
      </div>
    </div>
  )
}

const s = {
  wrap:    { minHeight:'100vh', background:'linear-gradient(165deg,#161922,#0e1119)', color:'#e8eef4', padding:'1.1rem 1.1rem 3rem', maxWidth:640, margin:'0 auto' },
  top:     { display:'flex', justifyContent:'flex-start' },
  back:    { background:'transparent', border:'none', color:'rgba(240,244,248,.6)', fontSize:'0.9rem', cursor:'pointer', padding:'0.4rem 0' },
  head:    { textAlign:'center', padding:'0.5rem 0 1rem' },
  icon:    { fontSize:'2rem' },
  title:   { margin:'0.3rem 0 0.4rem', fontSize:'1.4rem', fontWeight:800, color:'#f0f4f8' },
  sub:     { margin:0, fontSize:'0.88rem', lineHeight:1.55, color:'rgba(240,244,248,.55)' },
  card:    { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1.1rem', marginTop:'0.4rem' },
  body:    { margin:'0 0 0.8rem', fontSize:'0.95rem', lineHeight:1.6, color:'#eef2f7' },
  points:  { margin:'0.4rem 0 1rem', paddingLeft:'1.1rem', display:'flex', flexDirection:'column', gap:'0.35rem' },
  point:   { fontSize:'0.88rem', lineHeight:1.5, color:'rgba(240,244,248,.8)' },
  error:   { color:'#ffb4b4', fontSize:'0.82rem', margin:'0 0 0.6rem' },
  primary: { width:'100%', background:'#8b5cf6', border:'none', color:'#fff', fontSize:'0.95rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.85rem 1rem', cursor:'pointer' },
}
